import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  categoryMatchesSearch,
  countActiveServicesFilters,
  toLowerKey,
  DEFAULT_GUESTERIA_SERVICES_FILTERS,
  AVAILABILITY_OPTIONS,
  SERVICE_STATUS_OPTIONS,
  type GuesteriaServicesFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesFilters";
import { GuesteriaServicesFilterButton } from "@/components/pages/guesteria-partners/components/GuesteriaServicesFilterButton";
import type {
  PartnerServiceCategory,
  PriceRange,
} from "@/types/partner-settings";
import { ServiceStatus } from "@/types/partner-settings";
import { cn } from "@/lib/utils";

const CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const SECTION_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

const SECTION_PANEL_CLASS =
  "overflow-hidden rounded-md border border-[#E5E5E5] bg-white";

const OPTION_ROW_CLASS =
  "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal leading-normal text-[#171717]";

function FilterCheckboxRow({
  checked,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  label: string;
  onCheckedChange: () => void;
}): React.JSX.Element {
  return (
    <label
      className={cn(
        OPTION_ROW_CLASS,
        checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={CHECKBOX_CLASS}
      />
      <span>{label}</span>
    </label>
  );
}

function ExpandableSection({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="span"
        className={SECTION_TRIGGER_CLASS}
        onClick={onToggle}
      >
        {title}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-[#737373] transition-transform",
            expanded && "rotate-180",
          )}
        />
      </Button>
      {expanded ? children : null}
    </div>
  );
}

export type GuesteriaServicesFiltersPopoverProps = {
  applied: GuesteriaServicesFiltersState;
  onApply: (next: GuesteriaServicesFiltersState) => void;
  categories: PartnerServiceCategory[];
  priceRange: PriceRange | null;
};

export function GuesteriaServicesFiltersPopover({
  applied,
  onApply,
  categories,
  priceRange,
}: GuesteriaServicesFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] =
    React.useState<GuesteriaServicesFiltersState>(applied);
  const [expandedSections, setExpandedSections] = React.useState<
    Record<string, boolean>
  >({
    featured: false,
    status: false,
    price: false,
    category: false,
    availability: false,
  });

  const [categorySearch, setCategorySearch] = React.useState("");
  const activeCount = countActiveServicesFilters(draft);

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
  }, [applied]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleTag = (tagId: number) => {
    setDraft((prev) => {
      const has = prev.selectedTags.includes(tagId);
      const selectedTags = has
        ? prev.selectedTags.filter((t) => t !== tagId)
        : [...prev.selectedTags, tagId];
      return { ...prev, selectedTags };
    });
  };

  const toggleStatus = (status: ServiceStatus) => {
    setDraft((prev) => {
      const has = prev.statuses.includes(status);
      const statuses = has
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses };
    });
  };

  const removeTagChip = (tagId: number) => {
    setDraft((prev) => ({
      ...prev,
      selectedTags: prev.selectedTags.filter((t) => t !== tagId),
    }));
  };

  const filteredCategories = categories.filter((cat) =>
    categoryMatchesSearch(cat, categorySearch),
  );

  const minPriceNum = priceRange?.min ?? 0;
  const maxPriceNum = priceRange?.max ?? 1000;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          syncDraftFromApplied();
        }
      }}
    >
      <PopoverTrigger asChild>
        <GuesteriaServicesFilterButton />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[320px] max-h-[min(560px,calc(100vh-120px))] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base font-semibold leading-normal text-[#171717]">
              {activeCount > 0
                ? t("partners.services.filterPopover.filtersWithCount", {
                    count: activeCount,
                  })
                : t("partners.services.filterPopover.filters")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("partners.services.filterPopover.close")}
            >
              <X className="size-5" />
            </Button>
          </div>

          {/* Active filters chips */}
          {activeCount > 0 ? (
            <div className="flex flex-wrap gap-2">
              {draft.searchQuery.trim() ? (
                <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]">
                  <span className="truncate">{draft.searchQuery.trim()}</span>
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={() =>
                      setDraft((prev) => ({ ...prev, searchQuery: "" }))
                    }
                    aria-label={t(
                      "partners.services.filterPopover.clearSearch",
                    )}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ) : null}
              {draft.selectedTags
                .map(
                  (tagId) =>
                    categories.find((c) => c.tagId === tagId)?.name || null,
                )
                .filter(Boolean)
                .map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                  >
                    {name}
                    <Button
                      type="button"
                      variant="span"
                      className="rounded p-0.5 hover:bg-[#DDD6FE]"
                      onClick={() => {
                        const tagId = categories.find(
                          (c) => c.name === name,
                        )?.tagId;
                        if (tagId) removeTagChip(tagId);
                      }}
                      aria-label={t("partners.services.filterPopover.remove")}
                    >
                      <X className="size-3.5 shrink-0" />
                    </Button>
                  </span>
                ))}
            </div>
          ) : null}
        </div>

        {/* Scrollable content */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
          {/* Global search field */}
          <div className="relative shrink-0">
            <Input
              type="search"
              icon={<Search className="size-4 text-[#737373]" />}
              placeholder={t(
                "partners.services.filterPopover.searchPlaceholder",
              )}
              value={draft.searchQuery}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, searchQuery: e.target.value }))
              }
              className="h-9 border-[#E5E5E5] pr-9 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
            />
            {draft.searchQuery ? (
              <Button
                type="button"
                variant="span"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
                onClick={() =>
                  setDraft((prev) => ({ ...prev, searchQuery: "" }))
                }
                aria-label={t("partners.services.filterPopover.clearSearch")}
              >
                <X className="size-4" />
              </Button>
            ) : null}
          </div>

          {/* Featured section */}
          <ExpandableSection
            title={t("partners.services.filterPopover.featured")}
            expanded={expandedSections.featured}
            onToggle={() => toggleSection("featured")}
          >
            <div className={cn(SECTION_PANEL_CLASS, "p-1")}>
              <FilterCheckboxRow
                checked={draft.isRecommended === true}
                label={t("partners.services.filterPopover.topsOnly")}
                onCheckedChange={() =>
                  setDraft((prev) => ({
                    ...prev,
                    isRecommended:
                      prev.isRecommended === true ? undefined : true,
                  }))
                }
              />
              <FilterCheckboxRow
                checked={draft.isRecommended !== true}
                label={t("partners.services.filterPopover.all")}
                onCheckedChange={() =>
                  setDraft((prev) => ({
                    ...prev,
                    isRecommended: undefined,
                  }))
                }
              />
            </div>
          </ExpandableSection>

          {/* Status section */}
          <ExpandableSection
            title={t("partners.services.filterPopover.statusTitle")}
            expanded={expandedSections.status}
            onToggle={() => toggleSection("status")}
          >
            <div className={cn(SECTION_PANEL_CLASS, "p-1")}>
              {SERVICE_STATUS_OPTIONS.map((status) => (
                <FilterCheckboxRow
                  key={status}
                  checked={draft.statuses.includes(status)}
                  label={t(
                    `partners.services.filterPopover.status.${toLowerKey(status)}`,
                  )}
                  onCheckedChange={() => toggleStatus(status)}
                />
              ))}
            </div>
          </ExpandableSection>

          {/* Price section */}
          <ExpandableSection
            title={t("partners.services.filterPopover.price")}
            expanded={expandedSections.price}
            onToggle={() => toggleSection("price")}
          >
            <div className={cn(SECTION_PANEL_CLASS, "space-y-3 px-3 py-3")}>
              <Slider
                min={minPriceNum}
                max={maxPriceNum}
                step={1}
                value={[
                  draft.minPrice ?? minPriceNum,
                  draft.maxPrice ?? maxPriceNum,
                ]}
                onValueChange={(values: number[]) =>
                  setDraft((prev) => ({
                    ...prev,
                    minPrice: values[0],
                    maxPrice: values[1],
                  }))
                }
                className="w-full"
              />
              <div className="text-center text-sm font-normal text-[#171717]">
                €{draft.minPrice?.toFixed(2) ?? minPriceNum.toFixed(2)}-€
                {draft.maxPrice?.toFixed(2) ?? maxPriceNum.toFixed(2)}
              </div>
            </div>
          </ExpandableSection>

          {/* Category section */}
          <ExpandableSection
            title={t("partners.services.filterPopover.category")}
            expanded={expandedSections.category}
            onToggle={() => toggleSection("category")}
          >
            <div className={SECTION_PANEL_CLASS}>
              <div className="border-b border-[#E5E5E5] p-2">
                <Input
                  type="search"
                  icon={<Search className="size-4 text-[#737373]" />}
                  placeholder={t(
                    "partners.services.filterPopover.categorySearch",
                  )}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="h-8 border-[#E5E5E5] text-sm text-[#171717] shadow-none focus-visible:ring-0"
                />
              </div>
              <div className="max-h-[176px] overflow-y-auto p-1">
                {filteredCategories.length === 0 ? (
                  <p className="px-2 py-1.5 text-sm text-[#737373]">
                    {t("partners.services.filterPopover.noCategoriesFound")}
                  </p>
                ) : null}
                {filteredCategories.map((cat) => (
                  <FilterCheckboxRow
                    key={cat.tagId}
                    checked={draft.selectedTags.includes(cat.tagId)}
                    label={cat.name}
                    onCheckedChange={() => toggleTag(cat.tagId)}
                  />
                ))}
              </div>
            </div>
          </ExpandableSection>

          {/* Availability section */}
          <ExpandableSection
            title={t("partners.services.filterPopover.availabilityTitle")}
            expanded={expandedSections.availability}
            onToggle={() => toggleSection("availability")}
          >
            <div className={cn(SECTION_PANEL_CLASS, "p-1")}>
              {AVAILABILITY_OPTIONS.map((avail) => {
                const selected = draft.availability === avail;
                return (
                  <button
                    key={avail}
                    type="button"
                    onClick={() =>
                      setDraft((prev) => ({
                        ...prev,
                        availability: selected ? undefined : avail,
                      }))
                    }
                    className={cn(
                      OPTION_ROW_CLASS,
                      selected ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                    )}
                  >
                    {t(
                      `partners.services.filterPopover.availability.${toLowerKey(avail)}`,
                    )}
                  </button>
                );
              })}
            </div>
          </ExpandableSection>
        </div>

        {/* Footer buttons */}
        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_GUESTERIA_SERVICES_FILTERS);
              onApply(DEFAULT_GUESTERIA_SERVICES_FILTERS);
            }}
          >
            {t("partners.services.filterPopover.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            {t("partners.services.filterPopover.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
